import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Eye, Upload } from 'lucide-react';
import { branchWithdrawals, branchUpdateWithdraw, getBranchUser } from '../../services/branchApi';
import TxnDetailModal from './TxnDetailModal';

const statusColor: any = { Done: 'success', 'In Queue': 'warning', Pending: 'warning', Rejected: 'error' };
const PENDING = ['In Queue', 'Pending'];
const money = (v: any) => (v == null ? '-' : `₹${Number(v).toLocaleString()}`);

export default function BranchWithdrawals() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [detail, setDetail] = useState<any | null>(null);
  const [action, setAction] = useState<{ row: any; kind: 'Done' | 'Rejected' } | null>(null);
  const [remark, setRemark] = useState('');
  const [proof, setProof] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  // The Super Master and any agent/executive granted the withdraw capability can act.
  const user = getBranchUser();
  const canAct = !!(user?.isManager || user?.canWithdraw);

  const load = async () => {
    setLoading(true);
    try { setRows((await branchWithdrawals({ status })).data || []); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const open = (row: any, kind: 'Done' | 'Rejected') => { setAction({ row, kind }); setRemark(''); setProof(null); };
  const submit = async () => {
    if (!remark.trim()) return;
    if (action!.kind === 'Done' && !proof) { alert('A payout-proof image is required'); return; }
    setBusy(true);
    try {
      await branchUpdateWithdraw(action!.row.id, action!.kind, remark.trim(), proof);
      setAction(null); await load();
    } catch (e: any) { alert(e.message); } finally { setBusy(false); }
  };

  const cols = ['SN#', 'Requested', 'Client', 'Bank/UPI', 'Image', 'Amount', 'Paid', 'Remark', 'Action Date', 'Action By', 'Status', ...(canAct ? ['Actions'] : [])];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Manual Withdraw{canAct ? '' : ' Report'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {canAct ? 'Pay the user, upload proof, then mark Done — or reject to refund' : 'All withdraw requests for this branch'}
          </Typography>
        </Box>
        <TextField select size="small" label="Status" sx={{ minWidth: 160 }}
          value={status} onChange={(e) => setStatus(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="In Queue">In Queue</MenuItem>
          <MenuItem value="Done">Done</MenuItem>
          <MenuItem value="Rejected">Rejected</MenuItem>
        </TextField>
      </Stack>
      {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
        <Table size="small">
          <TableHead>
            <TableRow>{cols.map((h) => <TableCell key={h} sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.id} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.date ? new Date(r.date).toLocaleString() : '-'}</TableCell>
                <TableCell>{r.username}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.upi_id || `${r.bank_name || ''} ${r.account_number || ''}`.trim() || '-'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => setDetail(r)} title="View details & proof"><Eye size={16} /></IconButton>
                </TableCell>
                <TableCell>{money(r.amount)}</TableCell>
                <TableCell>{r.status === 'Done' ? money(r.approved_amount ?? r.amount) : money(0)}</TableCell>
                <TableCell sx={{ fontSize: 12, maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.remark || ''}>{r.remark || '-'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.handled_at ? new Date(r.handled_at).toLocaleString() : '-'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.action_by || '-'}</TableCell>
                <TableCell><Chip size="small" color={statusColor[r.status]} label={r.status} /></TableCell>
                {canAct && (
                  <TableCell>
                    {PENDING.includes(r.status) && (
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" color="success" onClick={() => open(r, 'Done')}>Approve</Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => open(r, 'Rejected')}>Reject</Button>
                      </Stack>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={cols.length} align="center" sx={{ py: 3, color: 'text.secondary' }}>No withdrawals.</TableCell></TableRow>}
          </TableBody>
        </Table>
      )}

      <TxnDetailModal open={!!detail} onClose={() => setDetail(null)} kind="withdraw" row={detail} />

      <Dialog open={!!action} onClose={() => setAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{action?.kind === 'Done' ? 'Mark paid (Done)' : 'Reject withdrawal'} · {action?.row?.username}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {money(action?.row?.amount)} · {action?.kind === 'Done' ? 'upload proof of the bank transfer you made.' : 'the amount will be refunded to the user.'}
          </Typography>
          {action?.kind === 'Done' && (
            <Button component="label" variant="outlined" startIcon={<Upload size={16} />} sx={{ mb: 2 }} fullWidth>
              {proof ? proof.name : 'Upload payout proof image'}
              <input hidden type="file" accept="image/*" onChange={(e) => setProof(e.target.files?.[0] || null)} />
            </Button>
          )}
          <TextField autoFocus fullWidth size="small" label="Remark (required)" value={remark}
            onChange={(e) => setRemark(e.target.value)} multiline minRows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)}>Cancel</Button>
          <Button variant="contained" color={action?.kind === 'Done' ? 'success' : 'error'}
            disabled={busy || !remark.trim() || (action?.kind === 'Done' && !proof)} onClick={submit}>
            {busy ? 'Saving…' : action?.kind === 'Done' ? 'Mark Done' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
